import { AWSCShortbread } from "@amzn/shortbread";
import CookieDefinitions from "@amzn/shortbread/dist/definitions/cookie-definitions";

/**
 * An external store to track the shortbread client to escape a
 * circular dependency we've found ourselves in. Shortbread initialization needs
 * to know what locale we've selected, and locale selection needs to establish
 * if we have permissions to set or read the locale cookie.
 *
 * By externalizing this we allow the locale selection to get the shortbread client
 * after it's initialized lower in the tree. We can't pass both by Context as context only
 * points downwards.
 *
 * @see https://react.dev/reference/react/useSyncExternalStore for more information on this pattern.
 */

export type Shortbread = Pick<
  ReturnType<typeof AWSCShortbread>,
  "customizeCookies" | "checkForCookieConsent" | "getConsentCookie" | "access" | "hasConsent"
>;

const FALLBACK_SHORTBREAD = {
  customizeCookies: () => {},
  checkForCookieConsent: () => {},
  getConsentCookie: () => ({
    essential: true,
    performance: false,
    functional: false,
    advertising: false,
  }),
  access: () => {},
  hasConsent: () => false,
};

export type OnConsentUpdated = (callback: () => void) => () => boolean;

// Keeping track of past shortbread values for debugging purposes.
const clientJournal: Shortbread[] = [FALLBACK_SHORTBREAD];
const listeners = new Set<() => void>([]);

export const setShortbreadClient = (shortbread: Shortbread) => {
  if (shortbread !== clientJournal[0]) {
    clientJournal.unshift(shortbread);
    listeners.forEach((listener) => listener());
    return true;
  }
  return false;
};

export const subscribe = (listener: () => void) => (listeners.add(listener), () => listeners.delete(listener));
export const getSnapshot = () => clientJournal[0];

const consentCallbacks = new Set<(c: CookieDefinitions.ConsentCookie) => void>([]);
export const onConsentUpdated = (callback: any) => consentCallbacks.add(callback);
export const notifyConsentUpdated = (c: CookieDefinitions.ConsentCookie) => consentCallbacks.forEach((cb) => cb(c));

/**
 * As this is in the global scope we need a mechanism
 * to clear for testing purposes. This isn't meant to be used
 * in production.
 */
export const clear = () => {
  listeners.clear();
  consentCallbacks.clear();
  clientJournal.length = 0;
};
